import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import { useTheme } from '../context/ThemeContext';
import { CURRENCY } from '../config/currency';

interface Invoice {
  id: number;
  order: { 
    id: number;
    customOrderId?: string;
  };
  createdAt: string;
  total: number;
}

const CustomerInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useTheme();
  const { components: { card }, gray, primary, text, button } = theme;

  useEffect(() => {
    setLoading(true);
    console.log('Fetching customer invoices...');
    api.get<Invoice[]>('/invoices')
      .then(res => {
        console.log('Customer invoices response:', res.data);
        // Ensure we always set an array
        const invoiceData = Array.isArray(res.data) ? res.data : [];
        
        // Sort invoices by createdAt descending (newest first)
        const sortedInvoices = invoiceData.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setInvoices(sortedInvoices);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching invoices:', err);
        setError(err.response?.data?.message || err.message || 'Failed to fetch invoices.');
        setInvoices([]); // Set empty array on error
        setLoading(false);
      });
  }, []);

  const renderInvoiceList = () => {
    if (loading) {
      return <p style={{ color: gray[500] }}>Loading invoices...</p>;
    }

    if (error) {
      return (
        <p
          className="p-3 border rounded-md"
          style={{
            backgroundColor: button.danger.bg,
            color: button.danger.text,
            borderColor: button.danger.bg,
          }}
        >
          {error}
        </p>
      );
    }

    if (invoices.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-400 text-5xl mb-4">📄</div>
          <p className="text-lg font-semibold mb-2" style={{ color: gray[700] }}>No invoices found</p>
          <p className="text-sm" style={{ color: gray[500] }}>
            Invoices are generated when orders are marked as completed.
          </p>
          <p className="text-sm mt-2" style={{ color: gray[500] }}>
            If you have completed orders but don't see invoices here, please contact support.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {invoices.map(inv => (
          <div
            key={inv.id}
            className="shadow-lg rounded-xl p-5 transition-all hover:shadow-xl"
            style={{ backgroundColor: card.background, color: text }}
          >
            <div className="flex flex-col sm:flex-row justify-between sm:items-center">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: primary }}>
                  Invoice #{inv.id}
                </h3>
                <p className="text-sm" style={{ color: gray[600] }}>Order ID: {inv.order.customOrderId || inv.order.id}</p>
                <p className="text-sm" style={{ color: gray[500] }}>
                  Date: {new Date(inv.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="mt-3 sm:mt-0 flex flex-col sm:items-end">
                <p className="text-lg font-bold mb-2 sm:mb-1" style={{ color: text }}>
                  Total: {CURRENCY.symbol}{inv.total.toFixed(2)}
                </p>
                <Link
                  to={`/invoices/${inv.id}`}
                  className="px-4 py-2 text-sm font-medium rounded-md shadow-sm transition-colors hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-background-primary)] focus:ring-offset-2 focus:ring-offset-[var(--color-background-surface)]"
                  style={{ backgroundColor: button.primary.bg || primary, color: button.primary.text }}
                >
                  View Details
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <CardLayoutTheme
      title="Invoices"
      categories={[{ id: 1, name: 'All Invoices' }]} // Dummy category
      renderItems={renderInvoiceList}
      sidebarTitle="Invoice Status"
    />
  );
};

export default CustomerInvoices;

